import { addDays } from "date-fns";
import StatefulSettings from "../controllers/StatefulSettings";
import {
  ITaskItem,
  IPenaltyItem,
  ITaskVoteConfig,
  IRouletteRequest,
  ItemAdditionOptions,
  BaseBasketItem,
  IShowNotificationState,
  AuditMessage,
  ISettingsItem,
  TaskBasketItem,
  PenaltyBasketItem,
  PilloryTopic,
} from "../ts/Definitions";
import {
  AuditActionTypes,
  TraceLevel,
  PendingUpdateType,
} from "../ts/enums";
import {
  NotificationType,
  sendRouletteAddedMessageToOwner,
  sendRoulettePilloryAddedMessageToOwner,
  sendRouletteVoteAddedMessageToOwner,
} from "../controllers/messaging";
import { convertToCountTypeString, getPilloryTopicDefaults } from "../utilities/utils";

/**
 * RouletteManager handles all roulette wheel functionality including:
 * - Opening roulette dialogs for task/penalty selection
 * - Processing roulette results (tasks, penalties, votes, pillories)
 * - Handling vote-related roulette outcomes
 */
export class RouletteManager {
  private settings: StatefulSettings;
  private showMessage: (args: IShowNotificationState) => void;
  private onTaskCompleted: (key: string) => void;
  private onAddToSchedule: (
    item: ITaskItem | IPenaltyItem,
    reason: string,
    options?: ItemAdditionOptions
  ) => void;

  constructor(
    settings: StatefulSettings,
    showMessage: (args: IShowNotificationState) => void,
    onTaskCompleted: (key: string) => void,
    onAddToSchedule: (
      item: ITaskItem | IPenaltyItem,
      reason: string,
      options?: ItemAdditionOptions
    ) => void
  ) {
    this.settings = settings;
    this.showMessage = showMessage;
    this.onTaskCompleted = onTaskCompleted;
    this.onAddToSchedule = onAddToSchedule;
  }

  /**
   * Opens the roulette dialog for the user to spin
   */
  public handleRouletteTask(
    item: ITaskItem | IPenaltyItem,
    options?: string[]
  ): void {
    if (!item && !options) return;
    if (item)
      this.settings.updateRouletteDialog({
        options,
        open: true,
        rouletteTask: item,
        title: item.name,
        description: item.description,
        itemType: item.itemType,
      });
  }

  /**
   * Processes the result from a roulette wheel spin
   */
  public handleRouletteResult(
    item: string | ITaskItem | IPenaltyItem | ITaskVoteConfig,
    roulette: IRouletteRequest,
    options?: ItemAdditionOptions
  ): void {
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Session,
        "Roulette Wheel Result Handling Started",
        "Roulette Wheel routine invoked",
        TraceLevel.info
      )
    );

    this.settings.updateRouletteDialog(false);
    const basket = this.settings.getBasketItems(true);
    const rouletteKey = roulette.rouletteTask.key;
    const basketItem = [
      ...(basket.pendingItems || []),
      ...(basket.tasks || []),
      ...(basket.penalties || []),
      ...(basket.permissions || []),
      ...(basket.events || []),
    ].find((i) => i.key === rouletteKey);
    if (!basketItem) return;
    const hasRouletteOptions = !!roulette.options && roulette.options.length > 0;

    if (!hasRouletteOptions) {
      this.onTaskCompleted(basketItem.key);

      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Session,
          basketItem.name,
          `Adding result for roulette wheel spin for ${(item as ISettingsItem).name
          }`,
          TraceLevel.info
        )
      );

      if (options && options.isPillory) {
        this.handleRoulettePillory(item as ITaskItem | IPenaltyItem, basketItem);
      } else if (options && options.isVote && (item as ITaskVoteConfig).taskKey) {
        this.handleRouletteVoteTask(item as ITaskVoteConfig, basketItem);
      } else {
        this.handleRouletteTaskAddition(item as ITaskItem | IPenaltyItem, basketItem);
      }
    } else {
      this.handleRouletteOptionSelection(item?.toString(), basketItem);
    }

    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Session,
        "Roulette Wheel Result Handling Completed",
        "Roulette Wheel routine finished",
        TraceLevel.info
      )
    );
  }

  /**
   * Handles roulette result that adds a pillory topic
   */
  private handleRoulettePillory(
    settingsItem: ITaskItem | IPenaltyItem,
    basketItem: BaseBasketItem
  ): void {
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Session,
        basketItem.name,
        `Adding pillory for roulette wheel spin for ${settingsItem.name}`,
        TraceLevel.info
      )
    );

    const { countType, count } = getPilloryTopicDefaults(settingsItem);
    const pendingItem = new PilloryTopic({
      updateType: PendingUpdateType.add,
      item: settingsItem,
      name: settingsItem.name,
      reason: "Wheel Of Fortune",
      username: this.settings.getControl().username,
      userid: this.settings.getControl().userid,
      ownername: this.settings?.getControl().getOwner().username,
      countType,
      incrementCount: count,
      pilloryEndTime: addDays(new Date(), 1).toISOString(),
      isRoulette: true,
      currentTotal: count,
      votedMembers: [],
      itemAdditionOptions: { isPillory: true, isRoulette: true },
      voteOptions: {},
    });

    try {
      this.settings.updatePilloryTopic(pendingItem);
    } catch (error: unknown) {
      console.error("Failed to update pillory topic:", error);
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Session,
          settingsItem.name,
          `Failed to update pillory topic: ${error instanceof Error ? error.message : String(error)
          }`,
          TraceLevel.error
        )
      );
    }

    sendRoulettePilloryAddedMessageToOwner(this.settings, settingsItem);
    this.showMessage({
      message: "Penalty Sent To Pillory For Voting",
      persist: true,
      notificationType: NotificationType.PilloryInfo,
    });
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Session,
        basketItem.name,
        `Roulette chose to send pillory item - ${settingsItem.name}`,
        TraceLevel.info
      )
    );
  }

  /**
   * Handles roulette result that modifies a vote
   */
  private handleRouletteVoteTask(
    item: ITaskVoteConfig,
    basketItem: BaseBasketItem
  ): void {
    const settingsItem = item as ITaskVoteConfig;
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Session,
        basketItem.name,
        `Adding vote for roulette wheel spin for ${settingsItem.taskKey}`,
        TraceLevel.info
      )
    );

    if (!settingsItem.taskKey) return;
    const task = this.settings.getTask(settingsItem.taskKey);
    if (!task) return;

    this.settings.voteUpdate(settingsItem);
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Session,
        basketItem.name,
        `Roulette chose to vote on item - ${task.name}`,
        TraceLevel.info
      )
    );

    sendRouletteVoteAddedMessageToOwner(this.settings, task, settingsItem);
    this.showMessage({
      message: `Vote on ${task.name} ${settingsItem.isUp ? "increased" : "descreased"
        } by ${convertToCountTypeString(
          settingsItem.voteIncrement,
          settingsItem.countType
        )}`,
      persist: true,
      notificationType: NotificationType.VotedItem,
    });
  }

  /**
   * Handles roulette result that adds a task/penalty to schedule
   */
  private handleRouletteTaskAddition(
    settingsItem: ITaskItem | IPenaltyItem,
    basketItem: BaseBasketItem
  ): void {
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Session,
        basketItem.name,
        `Adding task for roulette wheel spin for ${settingsItem.name} - calling add to schedule`,
        TraceLevel.info
      )
    );

    this.onAddToSchedule(
      settingsItem,
      "This task was added to the session from a result of a roulette wheel spun by the member. The tasks are within the limits of the member and valid today based on the members diary",
      { isRoulette: true }
    );

    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Session,
        basketItem.name,
        `Roulette chose to add item - ${settingsItem.name} - completed add to schedule`,
        TraceLevel.info
      )
    );

    sendRouletteAddedMessageToOwner(this.settings, settingsItem);
    this.showMessage({
      message: "Wheel item added to schedule",
      notificationType: NotificationType.ScheduleInfo,
    });
  }

  /**
   * Handles roulette result that selects an option from available choices
   */
  private handleRouletteOptionSelection(
    selectedOption: string | undefined,
    basketItem: BaseBasketItem
  ): void {
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Session,
        basketItem.name,
        `Option choosen from Roulette - ${selectedOption}`,
        TraceLevel.info
      )
    );

    const itemToPlay = basketItem as TaskBasketItem | PenaltyBasketItem;
    itemToPlay.selectedCondition = selectedOption;
    this.settings.updateTaskState(itemToPlay);
    this.showMessage({
      message: "Item updated in schedule",
      notificationType: NotificationType.ScheduleInfo,
    });
  }
}
